import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { ArrowLeft, Save, MapPin } from 'lucide-react';
import { getResort, updateResort, uploadResortFile } from '../services/resortAdminApi';
import { useAuth } from '../context/AuthContext';

interface ResortDetails {
  name: string;
  resort_name: string;
  resort_image?: string;
  default_warehouse?: string;
  delivery_locations?: Array<{ location?: string }>;
  services?: Array<{ service?: string }>;
  coming_soon?: number;
}

export const ResortDetails: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const { isReadOnly } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resort, setResort] = useState<ResortDetails | null>(null);
  const [formData, setFormData] = useState({
    resort_name: '',
    coming_soon: 0,
  });
  const [resortImage, setResortImage] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load resort details
  useEffect(() => {
    const loadResort = async () => {
      if (!name) return;

      setLoading(true);
      try {
        const result = await getResort(name);
        if (result) {
          setResort(result);
          setFormData({
            resort_name: result.resort_name || '',
            coming_soon: result.coming_soon ?? 0,
          });
          setResortImage(result.resort_image || '');
        }
      } catch (error: any) {
        setError(error.message || 'Failed to load resort');
      } finally {
        setLoading(false);
      }
    };
    loadResort();
  }, [name]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !name) return;

    setUploadingImage(true);
    try {
      const result = await uploadResortFile(file, name);
      if (result && result.file_url) {
        setResortImage(result.file_url);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;

    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await updateResort({
        resort_id: name,
        resort_name: formData.resort_name,
        resort_image: resortImage || undefined,
        clear_image: resortImage === '' && resort?.resort_image ? true : false,
        coming_soon: formData.coming_soon,
      });
      setSuccess('Resort updated successfully');

      // Reload resort to get updated data
      const updatedResort = await getResort(name);
      if (updatedResort) {
        setResort(updatedResort);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to update resort');
    } finally {
      setSaving(false);
    }
  };

  const getImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    const base = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) || window.location.origin;
    return `${base}${imageUrl}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-alps-primary"></div>
      </div>
    );
  }

  if (!resort) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Resort not found</p>
        <button
          onClick={() => navigate('/resorts')}
          className="mt-4 text-alps-primary hover:underline"
        >
          Back to Resorts
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/resorts')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Edit Resort</h1>
            <p className="mt-1 text-sm text-gray-600">{resort.resort_name}</p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <Card>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Resort Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.resort_name}
                onChange={(e) => setFormData({ ...formData, resort_name: e.target.value })}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resort Image
              </label>
              {resortImage ? (
                <div className="mt-2 space-y-2">
                  <div className="relative inline-block">
                    <img
                      src={getImageUrl(resortImage)}
                      alt="Resort"
                      className="h-32 w-32 object-cover rounded-md border border-gray-300"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setResortImage('')}
                      disabled={isReadOnly}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Remove</span>
                      ×
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <MapPin className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-alps-primary hover:text-alps-primary-dark">
                        <span>{uploadingImage ? 'Uploading...' : 'Upload an image'}</span>
                        <input
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={isReadOnly || uploadingImage}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center space-x-2">
                <input
                  id="resort-coming-soon"
                  type="checkbox"
                  checked={!!formData.coming_soon}
                  onChange={(e) => setFormData({ ...formData, coming_soon: e.target.checked ? 1 : 0 })}
                  disabled={isReadOnly}
                  className="h-4 w-4 text-green-600 border-gray-300 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <label htmlFor="resort-coming-soon" className="text-sm text-gray-700">
                  Coming Soon
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                When enabled, the resort will show a "Coming Soon" badge and be non-selectable in the app.
              </p>
            </div>

            {/* Delivery Locations (Read-only) */}
            {resort.delivery_locations && resort.delivery_locations.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Locations
                </label>
                <div className="flex flex-wrap gap-2">
                  {resort.delivery_locations.map((loc, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                    >
                      {loc.location || 'Unknown'}
                    </span>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Delivery locations are managed in the Frappe backend
                </p>
              </div>
            )}

            {/* Services (Read-only) */}
            {resort.services && resort.services.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supported Services
                </label>
                <div className="flex flex-wrap gap-2">
                  {resort.services.map((service, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                    >
                      {service.service || 'Unknown'}
                    </span>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Services are managed in the Frappe backend
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || !formData.resort_name || isReadOnly}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};
